import { SettlementFullDetails, SettlementStatusEnum, TransferTypeEnum, uploadV2Apis } from "@payping/api";
import { CuToman, OpenBlank } from "@payping/icons";
import { getSearchCashOutSettlements } from "@payping/redux/actions";
import { FiltersType, GridifyConditionEnum, GridifyLogicalEnum, createQuery } from "@payping/utils/api";
import { dateFormats, toJalali } from "@payping/utils/dateUtils";
import { DashboardRoutes } from "@/router/constant";
import { submitQueryParams } from "@payping/utils/query-string";
import {
  Bank,
  Button,
  Copy,
  Space,
  Table,
  useMessagedAsyncFn,
  useMessagedAsyncRetry,
  Flex,
} from "@payping/web-components";
import { ColumnsType, TablePaginationConfig } from "@payping/web-components/components/Table/types";
import qs from "query-string";
import React, { ReactElement, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "@/utils/redux";
import { useAppTranslation } from "@/utils/useAppTranslation";
import { detailId, receiptId, tablePaginationId } from "@/assets/googleTrackIds";
import ReportSettlementDetails from "@/pages/Settlement/Report/Settlement/Detail";
import { ListNumberQueryParamsType } from "../Request/types";
import { SettlementType, StatusObj } from "./types";
import ListFilters from "./ListFilters";
import { formatLocalizedNumber } from "@payping/utilities-shared/number";

export interface SettlementRowType {
  key: React.Key;
  amount?: string;
  code?: string;
  requestCode?: string;
  repayDate?: string;
  reqDate?: string;
  status?: string;
  statusColor?: string;
  iban?: string;
  bankTransferReference?: string;
}

const List: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { filteredSettlementList } = useAppSelector((state) => state.cashOuts);
  const parsedSearch = qs.parse(location.search, { arrayFormat: "bracket" });
  const { code, destinationBank, iban, status, submittedTimeFrom, submittedTimeTo }: SettlementType = parsedSearch;
  const { pageNumber = 1, pageSize = 10 }: ListNumberQueryParamsType = qs.parse(location.search, {
    parseNumbers: true,
  });
  const { t } = useAppTranslation();
  const [downloadCode, setDownloadCode] = useState<string>();

  const [settlementDetails, setSettlementDetails] = useState<
    { show: false; data: undefined } | { show: true; data: SettlementFullDetails }
  >({ show: false, data: undefined });

  const { loading, retry } = useMessagedAsyncRetry(
    async () => {
      const filter: FiltersType = [
        {
          code: [GridifyConditionEnum.Equal, code],
          destinationBank: [GridifyConditionEnum.Equal, destinationBank],
          iban: [
            GridifyConditionEnum.Equal,
            // eslint-disable-next-line no-nested-ternary
            iban ? (!iban.toUpperCase().startsWith("IR") ? `IR${iban}` : iban) : undefined,
          ],
          status: [GridifyConditionEnum.Equal, status || [1, 2, 3], { parenthesis: true }],
          submittedTime: [GridifyConditionEnum.GreaterThan, submittedTimeFrom],
        },
        GridifyLogicalEnum.And,
        {
          submittedTime: [GridifyConditionEnum.LessThan, submittedTimeTo],
        },
      ];
      await dispatch(getSearchCashOutSettlements(pageNumber, pageSize, undefined, createQuery(filter)));
    },
    [code, submittedTimeFrom, submittedTimeTo, status, destinationBank, iban, pageNumber, pageSize],
    {
      success: false,
      useProgress: true,
    },
  );

  const searchRequest = (requestCode: string) => {
    navigate({
      pathname: DashboardRoutes.settlement.report.detail.url.replace(":code", requestCode || ""),
    });
  };

  const statusTag = (statusMap = status) => {
    if (statusMap) {
      const statusObj = StatusObj.filter((stat) => stat.no === parseInt(statusMap, 10));
      if (statusObj[0]) return statusObj[0].locale;
    }
    return t("unclear");
  };

  const [{ loading: downloadReportLoading }, downloadReport] = useMessagedAsyncFn(async (row) => {
    setDownloadCode(row.code);
    const newWindow = window.open(undefined, "_blank");
    const path = await uploadV2Apis.settlementReportPdfWithSettlementCodeGet(row.code);
    newWindow?.location?.assign?.(path);
  }, []);

  const pagination: TablePaginationConfig = {
    "data-google-track": tablePaginationId,
    "data-test": "report-request-settlement-list-pagination",
    onChange: (page, newSize): void => {
      const newValues = {
        ...parsedSearch,
        pageNumber: page,
        pageSize: newSize,
      };
      submitQueryParams({
        navigate,
        newValues,
        parsedSearch,
        pathname: location.pathname,
        retry,
      });
    },
    onShowSizeChange: (_currentPage, newSize): void => {
      const newValues = {
        ...parsedSearch,
        pageNumber: 1,
        pageSize: newSize,
      };
      submitQueryParams({
        navigate,
        newValues,
        parsedSearch,
        pathname: location.pathname,
        retry,
      });
    },
    pageNumber,
    pageSize,
    showTotal: (total, range): string => t("showTotal", { range: `${range[0]}-${range[1]}`, total }),
    total: filteredSettlementList?.count,
  };

  const columns: ColumnsType<SettlementRowType> = [
    {
      dataIndex: "fullName",
      key: "fullName",
      render(fullName: string) {
        return fullName || "-";
      },
      title: t("settlement.report.name"),
    },
    {
      dataIndex: "iban",
      key: "iban",
      render(Iban: string) {
        return (
          <Space>
            <Copy text={Iban || ""}>{Iban || "_"}</Copy>
            <Bank.Icon showTooltip iban={Iban || "-"} />
          </Space>
        );
      },
      title: t("settlement.report.shaba"),
      width: 300,
    },
    {
      dataIndex: "code",
      key: "code",
      render(_, tableData) {
        return <Copy text={tableData.code || ""}>{tableData.code}</Copy>;
      },
      title: t("settlement.trackingCode"),
    },
    {
      dataIndex: "status",
      key: "status",
      render: (value) => <span>{t(`settlement.status.${statusTag(value)}`)}</span>,
      title: t("status"),
    },
    {
      dataIndex: "amount",
      key: "amount",
      render: (value) => (
        <Flex align="center">
          <span>{formatLocalizedNumber({ value: Number(value) })}</span>
          <CuToman width={24} height={24} />
        </Flex>
      ),
      title: t("settlement.report.amount"),
      width: 230,
    },
    {
      dataIndex: "submittedTime",
      key: "submittedTime",
      render: (value) => <span>{toJalali({ date: value, toFormat: dateFormats.ui.jalaliDateTime }).string}</span>,
      title: t("settlement.requestDate"),
    },
    {
      dataIndex: "requestCode",
      key: "requestCode",
      render: (value, record) => {
        const recordType = record as SettlementRowType;
        return (
          <>
            <span>
              <Button onClick={() => searchRequest(recordType.requestCode || "-")} type="link" className="tw-inline">
                <OpenBlank width={12} />
              </Button>
              <Copy text={value || ""}>{value}</Copy>
            </span>
          </>
        );
      },
      title: t("settlement.code"),
    },

    {
      align: "left",
      dataIndex: "wage",
      key: "wage",
      render: (value) => (
        <Flex align="center">
          <span>{formatLocalizedNumber({ value: Number(value) })}</span>
          <CuToman width={24} height={24} />
        </Flex>
      ),
      title: t("settlement.wage"),
    },
    {
      dataIndex: "bankTransferReference",
      key: "bankTransferReference",
      render(value, tableData) {
        return (
          <>
            {value || "-"}
            <Copy text={tableData.bankTransferReference || ""}>{tableData.bankTransferReference}</Copy>
          </>
        );
      },
      title: t("settlement.bankTransferReference"),
    },
    {
      dataIndex: "transferType",
      key: "transferType",
      render: (value: number) => {
        if (value === TransferTypeEnum.ACH) return t("settlement.type.paya");
        if (value === TransferTypeEnum.NORMAL) return t("settlement.type.acountToAcount");
        return t("settlement.type.auto");
      },
      title: t("settlement.type.transfer"),
    },
    {
      dataIndex: "nearestACHTime",
      key: "nearestACHTime",
      render: (value) => (
        <span>
          {
            toJalali({ date: value, dateFormat: "YYYY-MM-DDTHH:mm:ssZ", toFormat: dateFormats.ui.jalaliDateTime })
              .string
          }
        </span>
      ),
      title: t("settlement.estimatedTime"),
    },
  ];

  const actions = (_: number, record: SettlementRowType): ReactElement[] => {
    const { code: recordCode } = record;
    const dataList = filteredSettlementList?.data;
    const result = (dataList || []).find((i: any) => i.code === recordCode);
    return [
      result ? (
        <Button
          type="link"
          key="detail"
          onClick={() => {
            setSettlementDetails({
              data: result,
              show: true,
            });
          }}
          className="tw-inline"
          data-google-track={detailId}
        >
          {t("common.details")}
        </Button>
      ) : (
        <span> - </span>
      ),
      Number(record.status) === SettlementStatusEnum.TRANSFERRED ? (
        <Button
          key="report"
          onClick={() => downloadReport(record)}
          type="link"
          loading={downloadReportLoading && downloadCode === record.code}
          className="tw-inline"
          data-google-track={receiptId}
        >
          {t("settlement.downloadReport")}
        </Button>
      ) : (
        <span> - </span>
      ),
    ];
  };

  return (
    <div>
      <Space direction={"vertical"} size={"middle"} className={"tw-w-full"}>
        <ListFilters retry={retry} loading={loading} />
        <Table<SettlementType>
          actions={actions}
          hasIdColumn
          dataSource={filteredSettlementList?.data}
          columns={columns}
          pagination={pagination}
          loading={loading}
        />
      </Space>
      {settlementDetails.show && (
        <ReportSettlementDetails
          open={settlementDetails.show}
          data={settlementDetails.data}
          onClose={() => setSettlementDetails({ data: undefined, show: false })}
        />
      )}
    </div>
  );
};

export default List;
